import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "motion/react";
import {
  ArrowRight,
  CalendarCheck,
  Clock,
  MapPin,
  Phone,
  ShieldCheck,
  Star,
} from "lucide-react";
import { Eyebrow } from "../components/Buttons";
import { EASE } from "../motion";
import { MAPS_URL, PHONE, PHONE_HREF } from "../config";
import "./Hero.css";

// Hero photo — uses the first gallery photo. Change the file name to use another.
const HERO_IMAGE = process.env.PUBLIC_URL + "/no1/1.png";

const MotionLink = motion.create(Link);

const item = {
  hidden: { opacity: 0, y: 28, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.9, ease: EASE },
  },
};
const press = {
  whileHover: { y: -2 },
  whileTap: { scale: 0.97, y: 0 },
  transition: { type: "spring", stiffness: 420, damping: 26 },
};

export default function Hero() {
  const ref = useRef(null);
  const [imgOk, setImgOk] = useState(true);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.25]);

  return (
    <section ref={ref} className="hx">
      <motion.div
        className="hx__blob"
        style={{ y: blobY }}
        aria-hidden="true"
        animate={{ scale: [1, 1.08, 1], rotate: [0, 8, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container hx__grid">
        {/* ---------- Image ---------- */}
        <div className="hx__media">
          <motion.div
            className="hx__frame"
            initial={{ clipPath: "inset(0% 100% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
          >
            <motion.div
              className="hx__img"
              style={{ y: imgY }}
              initial={{ scale: 1.18 }}
              animate={{ scale: 1.06 }}
              transition={{ duration: 1.8, ease: EASE, delay: 0.1 }}
            >
              {imgOk ? (
                <img
                  src={HERO_IMAGE}
                  alt="Nail art at No. 1 Nails & Spa, Manly"
                  onError={() => setImgOk(false)}
                />
              ) : (
                <div className="hx__placeholder" />
              )}
            </motion.div>
          </motion.div>

          <motion.div
            className="hx__badge"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 1.1,
            }}
          >
            <span className="hx__badge-icon">
              <ShieldCheck aria-hidden="true" />
            </span>
            <span>
              <strong>100% happiness</strong>
              <small>Guaranteed, every visit</small>
            </span>
          </motion.div>
        </div>

        {/* ---------- Text ---------- */}
        <motion.div
          className="hx__copy"
          style={{ opacity: fade }}
          initial="hidden"
          animate="show"
          variants={{
            show: { transition: { staggerChildren: 0.11, delayChildren: 0.4 } },
          }}
        >
          <motion.div variants={item} className="hx__rating">
            <span className="hx__stars" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} />
              ))}
            </span>
            Loved in Manly since 2009
          </motion.div>

          <motion.div variants={item}>
            <Eyebrow>MANLY · SYDNEY NSW</Eyebrow>
          </motion.div>

          <motion.h1 className="hx__title" variants={item}>
            Where{" "}
            <motion.span
              className="editorial hx__accent"
              initial={{ backgroundSize: "0% 100%" }}
              animate={{ backgroundSize: "100% 100%" }}
              transition={{ duration: 1.1, delay: 1.2, ease: EASE }}
            >
              beautiful nails
            </motion.span>{" "}
            meet peace of mind
          </motion.h1>

          <motion.p className="hx__sub" variants={item}>
            Premium nail care in the heart of Manly. World-class products,
            meticulous technique, and your 100% happiness — guaranteed.
          </motion.p>

          {/* ---------- 3 buttons ---------- */}
          <motion.div className="hx__ctas" variants={item}>
            <MotionLink
              to="/booking"
              className="hx-btn hx-btn--primary"
              {...press}
            >
              <CalendarCheck aria-hidden="true" />
              <span>Book online</span>
              <ArrowRight className="hx-btn__arrow" aria-hidden="true" />
            </MotionLink>
            <motion.a
              href={PHONE_HREF}
              className="hx-btn hx-btn--ghost"
              {...press}
              aria-label={`Call ${PHONE}`}
            >
              <Phone aria-hidden="true" />
              <span className="hx-btn__long">{PHONE}</span>
              <span className="hx-btn__short">Call</span>
            </motion.a>
            <motion.a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hx-btn hx-btn--ghost"
              {...press}
            >
              <MapPin aria-hidden="true" />
              <span>Directions</span>
            </motion.a>
          </motion.div>

          <motion.ul className="hx__facts" variants={item}>
            <li>
              <Clock aria-hidden="true" />
              Late night Thursday
            </li>
            <li>
              <MapPin aria-hidden="true" />
              5A Market Lane
            </li>
            <li>
              <CalendarCheck aria-hidden="true" />
              Walk-ins welcome
            </li>
          </motion.ul>
        </motion.div>
      </div>
    </section>
  );
}
